import {resolve} from "../../container";
import {QueryParameters} from "../../common/queryParameters";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, query, state} from "lit/decorators.js";
import {EopViewportSpinner} from "../../page/elements/spinner";
import {schedule} from "../../common/utils/promises";
import {type DOIResponse, MacherbusService} from "./macherbusService";
import type {Nullable} from "../../bootstrap/common/types";
import {UnLitElement, WithEopSlots} from "../../common/elements";
import {EopErrorHandler} from "../../common/errorHandler";

const VOTECODE_QUERY_PARAM = "optinid";
const ACTION_QUERY_PARAM = "action";

const validActions = ["confirm", "reject", "deadline-error", "error"] as const;
type Action = (typeof validActions)[number];

@customElement("eop-macherbus-followup")
export class EopMacherbusFollowUp extends WithEopSlots(UnLitElement) {

    @state()
    private activeSlot: Nullable<string>;
    @property({attribute: "voting-id"})
    private votingId: string;
    @property({attribute: "language"})
    private language: string;
    @property({attribute: "confirm-slot-name"})
    private confirmSlotName: string;
    @property({attribute: "reject-slot-name"})
    private rejectSlotName: string;
    @property({attribute: "error-slot-name"})
    private errorSlotName: string;

    @query("eop-viewport-spinner")
    private spinner: EopViewportSpinner;

    private readonly action: Nullable<Action>;
    private readonly voteCode: Nullable<string>;

    public constructor(
        private macherbusService: MacherbusService = resolve(MacherbusService),
        private queryParameters: QueryParameters = resolve(QueryParameters),
        private errorHandler: EopErrorHandler = resolve(EopErrorHandler)
    ) {
        super();
        this.activeSlot = null;
        this.action = this.validateAction(this.queryParameters.getString(ACTION_QUERY_PARAM));
        this.voteCode = this.queryParameters.getString(VOTECODE_QUERY_PARAM);
    }

    private validateAction(action: Nullable<string>): Nullable<Action> {
        if (action === null) {
            return null;
        }
        return (validActions as unknown as string[]).includes(action)
            ? action as Action
            : "error";
    }

    public firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        if (this.voteCode && this.action) {
            void this.actionRequest(this.voteCode);
        }
    }

    public render(): Nullable<TemplateResult> {
        return html`
            <eop-viewport-spinner></eop-viewport-spinner>
            ${this.renderActiveSlot()}
        `;
    }

    public renderActiveSlot(): Nullable<TemplateResult> {
        if (!this.activeSlot) {
            return null;
        }

        return html`
            ${this.resolveSlot(this.activeSlot)}
        `;
    }

    private async actionRequest(voteCode: string): Promise<void> {
        let request: Promise<DOIResponse>;

        if (this.action === "confirm") {
            request = this.macherbusService.confirm({votingId: this.votingId, lang: this.language, voteCode: voteCode});
        } else if (this.action === "reject") {
            request = this.macherbusService.reject({votingId: this.votingId, lang: this.language, voteCode: voteCode});
        } else {
            request = Promise.reject(new Error("Invalid Action"));
        }

        await schedule(this.performRequest(request))
            .as("action-request");
    }

    private async performRequest(request: Promise<DOIResponse>): Promise<void> {
        try {
            const response: DOIResponse = await this.spinner.spinWhile(request);

            if (response.success) {
                this.activeSlot = this.action === "confirm"
                    ? this.confirmSlotName
                    : this.rejectSlotName;
            } else {
                this.activeSlot = this.errorSlotName;
            }
        } catch (error) {
            this.activeSlot = this.errorSlotName;
            this.errorHandler.report(error);
        }
    }
}
